import React from "react";
import { Button, Flex, FlexProps, HStack, Icon } from "@chakra-ui/react";
import { GrFormNext, GrFormPrevious } from "react-icons/gr";
import { IoEllipsisHorizontalOutline } from "react-icons/io5";
import { useQueryParams } from "duck";
import { calculateRange } from "./duck";

export interface PaginationProps extends FlexProps {
  total: number | string;
  perPage?: number;
  hideOnSinglePage?: boolean;
}

const btnProps = {
  minW: "32px",
  maxW: "32px",
  h: "32px",
  as: Button,
  colorScheme: "black"
};

const Pagination: React.FC<PaginationProps> = ({
  total: initTotal,
  perPage = 20,
  hideOnSinglePage = true,
  ...rest
}) => {
  const [params, setQueryParams] = useQueryParams();
  const { pagination = {} } = params as any;

  const setPage = (currentPage: number) => {
    setQueryParams({
      ...params,
      pagination: { ...pagination, page: currentPage }
    });
  };

  const pageNumber = pagination.page || 1;
  const {
    prev,
    range,
    next,
    pageCount,
    hasFirstPage,
    hasLastPage,
    jumpPrevPage,
    jumpNextPage
  } = calculateRange(pageNumber, perPage, +initTotal);

  if (pageCount === 1 && hideOnSinglePage) {
    return null;
  }

  return (
    <Flex
      alignItems="center"
      p="16px"
      justifyContent="center"
      flexDirection={{ base: "column", sm: "row" }}
      {...rest}
    >
      <HStack spacing="12px">
        <Flex
          {...btnProps}
          variant="outline"
          onClick={() => setPage(prev)}
          isDisabled={pageNumber === 1}
        >
          <Icon as={GrFormPrevious} />
        </Flex>

        {hasFirstPage && (
          <Flex {...btnProps} variant="outline" onClick={() => setPage(1)}>
            1
          </Flex>
        )}

        {jumpPrevPage && (
          <Flex
            {...btnProps}
            variant="ghost"
            onClick={() => setPage(jumpPrevPage)}
          >
            <Icon as={IoEllipsisHorizontalOutline} />
          </Flex>
        )}

        {range.map(number => (
          <Flex
            key={number}
            {...btnProps}
            variant={number === pageNumber ? "solid" : "outline"}
            colorScheme={number === pageNumber ? "blue" : "black"}
            onClick={() => setPage(number)}
          >
            {number}
          </Flex>
        ))}

        {jumpNextPage && (
          <Flex
            {...btnProps}
            variant="ghost"
            onClick={() => setPage(jumpNextPage)}
          >
            <Icon as={IoEllipsisHorizontalOutline} />
          </Flex>
        )}

        {hasLastPage && (
          <Flex
            {...btnProps}
            variant="outline"
            onClick={() => setPage(pageCount)}
          >
            {pageCount}
          </Flex>
        )}

        <Flex
          {...btnProps}
          variant="outline"
          onClick={() => setPage(next)}
          isDisabled={pageNumber === pageCount}
        >
          <Icon as={GrFormNext} />
        </Flex>
      </HStack>
    </Flex>
  );
};

export default Pagination;
